import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { setUkek, setDecapsKey } from '@/store/slices/auth/authSlice';
import { setDerivedKey, setKeyPair, setPrivateKeyHash } from '@/store/slices/poc/pocAuthSlice';
import {
  startUpdate,
  setStage,
  setTotalFileKeys,
  fileKeyReEncrypted,
  fileKeyFailed,
  updateComplete,
} from '@/store/slices/passwordUpdate/passwordUpdateSlice';
import { hexToBytes, numbersToBytes, bytesToNumbers } from '@/utils/hex.utils';
import { addFile, getFile } from '@/utils/fileOperations';
import { setUkekInStorage } from '@/utils/sessionStorage';
import { SIX_TOPIC } from '@/config/networkConstants';
import type { UpdatePasswordPayload } from '@/types/passwordUpdate/PasswordUpdate';
import type { SixFilesApiResponse } from '@/types/six/SixDTOs';
import type { HeliaNode } from '@/types/poc/helia';
import type { LibP2pWithServices } from '@/types/poc/libp2p';
import type { MessagePayload } from '@/types/poc/message';
import type { Logger } from '@/utils/logger';
import type { AppDispatch } from '@/store';
import type { PocServices } from '@/types/shared/services.types';
import type { SixCatalogService } from '@/services/abstract/SixCatalogService';
import type { SixDetailService } from '@/services/abstract/SixDetailService';

const HEX_BASE = 16;
const HEX_PAD_LENGTH = 2;
const ENCRYPTION_FETCH_TIMEOUT_MS = 30_000;

interface HeliaFileKeyBlob {
  cipherText: number[];
  encryptedFileKey: {
    encryptedData: number[];
    iv: number[];
    authTag: number[];
  };
}

interface FileKeyToReEncrypt {
  sixId: string;
  fileId: string;
  fileKeyCID: string;
}

interface ReEncryptionContext {
  oldPrivateKey: Uint8Array;
  newPublicKey: Uint8Array;
  currentUserId: string;
  dispatch: AppDispatch;
  pocServices: PocServices;
  log: Logger;
}

interface CollectFileKeysParams {
  currentUserId: string;
  sixCatalogService: SixCatalogService;
  sixDetailService: SixDetailService;
  dispatch: AppDispatch;
  log: Logger;
}

/**
 * Collects all file keys across all sixes that need re-encryption
 * for the given user.
 */
async function collectFileKeysToReEncrypt(
  params: CollectFileKeysParams
): Promise<FileKeyToReEncrypt[]> {
  const { currentUserId, sixCatalogService, sixDetailService, dispatch, log } = params;

  log.info('Fetching all sixes...');
  const { sixes } = await sixCatalogService.fetchSixes();
  log.info(`Found ${sixes.length} six(es)`);

  const fileKeysToReEncrypt: FileKeyToReEncrypt[] = [];

  for (const six of sixes) {
    try {
      const filesResponse: SixFilesApiResponse = await sixDetailService.fetchFiles(six.id);
      for (const file of filesResponse.files) {
        if (currentUserId in file.encryptedFileKeys) {
          const userFileKeyCID = file.encryptedFileKeys[currentUserId];
          if (userFileKeyCID.length > 0) {
            fileKeysToReEncrypt.push({
              sixId: six.id,
              fileId: file.id,
              fileKeyCID: userFileKeyCID,
            });
          }
        }
      }
    } catch (fetchError) {
      const errorMessage = `Failed to fetch files for six ${six.id}: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`;
      log.error(errorMessage);
      dispatch(fileKeyFailed(errorMessage));
    }
  }

  log.info(`Found ${fileKeysToReEncrypt.length} file key(s) to re-encrypt`);
  return fileKeysToReEncrypt;
}

/**
 * Persists new key material to Redux store and session storage.
 */
function persistNewKeyMaterial(
  newUkek: string,
  newPrivateKey: Uint8Array,
  currentUserId: string,
  dispatch: AppDispatch
): void {
  dispatch(setUkek(newUkek));
  setUkekInStorage(currentUserId, newUkek);
  const newDecapsKeyHex = Array.from(newPrivateKey)
    .map(b => b.toString(HEX_BASE).padStart(HEX_PAD_LENGTH, '0'))
    .join('');
  dispatch(setDecapsKey(newDecapsKeyHex));
}

/**
 * Orchestration thunk for the full password update flow:
 * 1. Validate old password (client-side UKEK comparison)
 * 2. Re-register OPAQUE credentials with new password
 * 3. Generate and register new ML-KEM keys
 * 4. Re-encrypt all file keys with new ML-KEM keys
 * 5. Update local state (UKEK, decapsKey)
 */
export const updatePasswordThunk = createAppAsyncThunk(
  'auth/updatePassword',
  async(
    { oldPassword, newPassword }: UpdatePasswordPayload,
    { dispatch, getState, extra, rejectWithValue }
  ) => {
    const { authService, sixCatalogService, sixDetailService, userService, pocServices, loggerService } = extra;
    const { cryptoService } = pocServices;
    const log = loggerService.createLogger({ namespace: 'six3ro:password-update' });

    try {
      dispatch(startUpdate());

      const state = getState();
      const username = state.auth.username;
      const currentUserId = state.user.user?.id;

      if (currentUserId == null) {
        throw new Error('User not authenticated');
      }

      // ── Step 1: Validate old password ──
      log.info('Validating old password...');
      const oldUkek = await cryptoService.deriveKey(oldPassword, username);

      if (oldUkek !== state.auth.ukek) {
        log.warn('Old password validation failed — UKEK mismatch');
        dispatch(setStage({ stage: 'error', message: 'Current password is incorrect' }));
        return rejectWithValue({ message: 'Current password is incorrect' });
      }
      log.info('Old password validated');

      // ── Step 2: Update OPAQUE credentials ──
      dispatch(setStage({ stage: 'updatingCredentials', message: 'Updating credentials...' }));

      log.info('Re-registering OPAQUE credentials with new password...');
      await authService.setPassword({ username, password: newPassword });
      log.info('OPAQUE credentials updated');

      // ── Step 3: Derive new UKEK and generate new ML-KEM keys ──
      log.info('Deriving new UKEK...');
      const newUkek = await cryptoService.deriveKey(newPassword, username);

      log.info('Generating new ML-KEM key pair...');
      const newHashResult = await cryptoService.hashPassword(newPassword, username);
      const newKeyPair = await cryptoService.generateMlKemKeyPairFromCredentials(username, newPassword);
      const encryptedNewPrivateKey = await cryptoService.encryptPrivateKey(newKeyPair.privateKey, newHashResult);

      log.info('Updating ML-KEM keys on backend...');
      const publicKeyArray = Array.from(newKeyPair.publicKey);
      await authService.updateMlkemKeys(currentUserId, {
        encapsKey: JSON.stringify(publicKeyArray),
        decapsKey: encryptedNewPrivateKey,
      });
      log.info('ML-KEM keys updated');

      // Update Redux with new key material
      dispatch(setDerivedKey(newHashResult.keyHex));
      dispatch(setKeyPair(newKeyPair));
      dispatch(setPrivateKeyHash(encryptedNewPrivateKey));

      // ── Step 4: Get old ML-KEM private key for file key decryption ──
      dispatch(setStage({ stage: 'reEncryptingFileKeys', message: 'Preparing file key re-encryption...' }));

      // eslint-disable-next-line @typescript-eslint/init-declarations -- assigned in both branches below
      let oldPrivateKey: Uint8Array;
      if (state.auth.decapsKey.length > 0) {
        log.debug('Using cached old private key');
        oldPrivateKey = hexToBytes(state.auth.decapsKey);
      } else {
        log.debug('Fetching and decrypting old private key...');
        const { decapsKey: encryptedOldPrivateKeyHex } = await userService.getDecapsKey(currentUserId);
        const oldUkekBytes = hexToBytes(oldUkek);
        oldPrivateKey = await cryptoService.decryptPrivateKey(encryptedOldPrivateKeyHex, oldUkekBytes);
      }

      // ── Step 5: Collect all file keys to re-encrypt ──
      const fileKeysToReEncrypt = await collectFileKeysToReEncrypt({
        currentUserId, sixCatalogService, sixDetailService, dispatch, log,
      });

      if (fileKeysToReEncrypt.length === 0) {
        persistNewKeyMaterial(newUkek, newKeyPair.privateKey, currentUserId, dispatch);
        dispatch(updateComplete());
        log.info('Password updated (no file keys to re-encrypt)');
        return { processed: 0, total: 0, errors: [] };
      }

      dispatch(setTotalFileKeys(fileKeysToReEncrypt.length));

      // ── Step 6: Re-encrypt each file key ──
      const reEncryptionContext: ReEncryptionContext = {
        oldPrivateKey,
        newPublicKey: newKeyPair.publicKey,
        currentUserId,
        dispatch,
        pocServices,
        log,
      };

      for (const fileKey of fileKeysToReEncrypt) {
        try {
          await reEncryptOneFileKey(fileKey, reEncryptionContext);
          dispatch(fileKeyReEncrypted());
        } catch (reEncryptError) {
          const errorMessage = `Failed to re-encrypt file key for file ${fileKey.fileId} (six: ${fileKey.sixId}): ${reEncryptError instanceof Error ? reEncryptError.message : 'Unknown error'}`;
          log.error(errorMessage);
          dispatch(fileKeyFailed(errorMessage));
        }
      }

      // ── Step 7: Update local state ──
      persistNewKeyMaterial(newUkek, newKeyPair.privateKey, currentUserId, dispatch);
      dispatch(updateComplete());

      const finalState = getState();
      const result = {
        processed: finalState.passwordUpdate.processedFileKeyCount,
        total: fileKeysToReEncrypt.length,
        errors: finalState.passwordUpdate.errors,
      };
      log.info('Password update complete:', result);
      return result;

    } catch (error) {
      log.error('Password update failed:', error);
      dispatch(setStage({
        stage: 'error',
        message: error instanceof Error ? error.message : 'Password update failed',
      }));
      return rejectWithValue(
        error instanceof Error ? error.message : 'Unknown password update error'
      );
    }
  }
);

/**
 * Re-encrypts a single file key: fetches the old blob from Helia,
 * decrypts with old ML-KEM private key, re-encrypts with new ML-KEM
 * public key, stores new blob in Helia, and publishes gossipsub.
 */
async function reEncryptOneFileKey(
  fileKey: FileKeyToReEncrypt,
  ctx: ReEncryptionContext
): Promise<void> {
  const { oldPrivateKey, newPublicKey, currentUserId, pocServices, log } = ctx;
  const { p2pStackManagerService, messagingService, fileEncryptionService } = pocServices;

  // 1. Get Helia node for this six
  const helia: HeliaNode | null = p2pStackManagerService.getHeliaNode(fileKey.sixId);
  if (helia === null) {
    throw new Error(`Helia node not initialized for six: ${fileKey.sixId}`);
  }

  // 2. Fetch old encrypted file key blob from Helia
  log.debug(`Fetching file key CID: ${fileKey.fileKeyCID}`);
  const encryptionJsonBytes = await getFile(helia, fileKey.fileKeyCID, {
    signal: AbortSignal.timeout(ENCRYPTION_FETCH_TIMEOUT_MS),
  });
  const encryptionJsonString = new TextDecoder().decode(new Uint8Array(encryptionJsonBytes));
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- JSON parsed from trusted Helia blob
  const blob = JSON.parse(encryptionJsonString) as HeliaFileKeyBlob;

  // 3. Convert number arrays to Uint8Arrays
  const oldEncryption = {
    recipientId: currentUserId,
    cipherText: numbersToBytes(blob.cipherText),
    encryptedFileKey: {
      encryptedData: numbersToBytes(blob.encryptedFileKey.encryptedData),
      iv: numbersToBytes(blob.encryptedFileKey.iv),
      authTag: numbersToBytes(blob.encryptedFileKey.authTag),
    },
  };

  // 4. Re-encrypt: decrypt with old private key, encrypt with new public key
  log.debug(`Re-encrypting file key for file: ${fileKey.fileId}`);
  const reEncrypted = await fileEncryptionService.reEncryptFileKeyForRecipient(
    oldEncryption,
    oldPrivateKey,
    newPublicKey
  );

  // 5. Serialize and store new blob in Helia
  const newBlob: HeliaFileKeyBlob = {
    cipherText: bytesToNumbers(reEncrypted.cipherText),
    encryptedFileKey: {
      encryptedData: bytesToNumbers(reEncrypted.encryptedFileKey.encryptedData),
      iv: bytesToNumbers(reEncrypted.encryptedFileKey.iv),
      authTag: bytesToNumbers(reEncrypted.encryptedFileKey.authTag),
    },
  };
  const blobBytes = new TextEncoder().encode(JSON.stringify(newBlob));
  const blobFile = new File(
    [blobBytes],
    `filekey-${fileKey.fileId}-${currentUserId}.json`,
    { type: 'application/octet-stream' }
  );

  const addResult = await addFile(helia, blobFile);
  const newFileKeyCID = addResult.cid.toString();
  log.debug(`Stored new file key in Helia, CID: ${newFileKeyCID}`);

  // 6. Publish gossipsub message to notify HAP
  const libp2pNode: LibP2pWithServices | null = p2pStackManagerService.getLibp2pNode(fileKey.sixId);
  if (libp2pNode == null) {
    throw new Error(`LibP2P node not available for six: ${fileKey.sixId}`);
  }

  const message: MessagePayload = {
    type: 'file-key-ready-for-download',
    id: crypto.randomUUID(),
    fileId: fileKey.fileId,
    newFileKeyCID,
    newFileKeyForUserId: currentUserId,
    newFileKeyCreatorUserId: currentUserId,
    sixId: fileKey.sixId,
    sender: libp2pNode.peerId.toString(),
    timestamp: Date.now(),
  };

  await messagingService.publishMessage(
    libp2pNode,
    SIX_TOPIC(fileKey.sixId),
    message
  );

  log.info(`Re-encrypted file key for file: ${fileKey.fileId}, new CID: ${newFileKeyCID}`);
}
